import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { pingAPI } from "@/lib/ping-api";
import { queryKeys } from "@/lib/query-keys";
import type { InternalMessageListParams } from "@/types";

// Helper function to extract page number from next URL
const getPageFromUrl = (url: string | null): number => {
  if (!url) return 1;
  const urlObj = new URL(url);
  const page = urlObj.searchParams.get("page");
  return page ? parseInt(page, 10) : 1;
};

// Hook for fetching messages for a specific internal chat
export const useInternalMessages = (
  chatId: string,
  params?: Omit<InternalMessageListParams, "chat_id">
) => {
  const messageParams = {
    chat_id: chatId,
    ...params,
  };

  const query = useQuery({
    queryKey: queryKeys.ping.internalMessages.list(chatId, params),
    queryFn: () => pingAPI.internalMessages.list(messageParams),
    select: (data) => ({
      messages: data.results || [],
      pagination: {
        count: data.count,
        next: data.next,
        previous: data.previous,
      },
    }),
    enabled: !!chatId,
    retry: false,
    staleTime: 15000, // 15 seconds (shorter for messages)
    gcTime: 300000, // 5 minutes
  });

  return query;
};

// Hook for infinite scrolling messages for internal chats
export const useInternalMessagesInfinite = (
  chatId: string,
  pageSize: number = 50
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.ping.internalMessages.infiniteWithParams(
      chatId,
      pageSize
    ),
    queryFn: async ({ pageParam = 1 }) => {
      const response = await pingAPI.internalMessages.list({
        chat_id: chatId,
        page: pageParam,
        page_size: pageSize,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // Extract next page number from the next URL
      if (lastPage.next) {
        return getPageFromUrl(lastPage.next);
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      // For reverse pagination (older messages)
      if (firstPage.previous) {
        return getPageFromUrl(firstPage.previous);
      }
      return undefined;
    },
    select: (data) => {
      // Flatten all pages into a single array
      const allMessages = data.pages.flatMap((page) => page.results || []);

      // Deduplicate messages by ID (in case of overlapping pages or race conditions)
      const uniqueMessages = Array.from(
        new Map(allMessages.map((msg) => [msg.id, msg])).values()
      );

      return {
        messages: uniqueMessages,
        pagination: {
          count: data.pages[0]?.count || 0,
          next: data.pages[data.pages.length - 1]?.next || null,
          previous: data.pages[0]?.previous || null,
        },
        pages: data.pages,
      };
    },
    enabled: !!chatId,
    retry: false,
    staleTime: 15000, // 15 seconds
    gcTime: 300000, // 5 minutes
  });
};

// Hook for fetching initial messages (useful for chat initialization)
export const useInternalInitialMessages = (
  chatId: string,
  pageSize: number = 20
) => {
  return useQuery({
    queryKey: queryKeys.ping.internalMessages.list(chatId, {
      page: 1,
      page_size: pageSize,
    }),
    queryFn: () =>
      pingAPI.internalMessages.list({
        chat_id: chatId,
        page: 1,
        page_size: pageSize,
      }),
    select: (data) => data.results || [],
    enabled: !!chatId,
    retry: false,
    staleTime: 10000, // 10 seconds
  });
};
